/**
 * InvoiceDetailPanel Component
 *
 * Slide-over panel that displays comprehensive invoice details including
 * payment history, credit notes, and action buttons for recording payments
 * and creating credit notes.
 *
 * User Story: US-REC-001 - Invoice Reconciliation
 */

import { useState, useEffect, useMemo } from 'react'
import { useInvoiceBalance } from '@/hooks/useInvoiceBalance'
import { useCreditNotes } from '@/hooks/useCreditNotes'
import { InvoiceStatusBadge } from './InvoiceStatusBadge'
import { GoCardlessStatusBadge } from '@/components/GoCardlessStatusBadge'
import { PaymentHistoryTimeline } from './PaymentHistoryTimeline'
import { LegacyInvoiceBanner } from './LegacyInvoiceBanner'
import { CreditNoteModal } from '@/components/credit-notes/CreditNoteModal'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  CreditCardIcon,
  ReceiptIcon,
  CalendarIcon,
  UserIcon,
  RefreshCwIcon
} from 'lucide-react'
import { supabase } from '@/supabase-client'
import { parseDateString } from '@/utils/dateUtils'

/**
 * Format currency value for display
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount || 0)
}

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
const formatDate = (dateString) => {
  if (!dateString) return '-'
  return parseDateString(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

/**
 * InvoiceDetailPanel displays comprehensive invoice information
 * @param {Object} props - Component props
 * @param {string} props.invoiceId - Invoice ID to display
 * @param {boolean} props.open - Whether the panel is open
 * @param {function} props.onOpenChange - Callback when open state changes
 * @param {function} props.onRecordPayment - Callback to open payment recording dialog
 * @param {function} props.onDataChange - Callback when data changes (payment/credit added)
 */
export function InvoiceDetailPanel({
  invoiceId,
  open,
  onOpenChange,
  onRecordPayment,
  onDataChange
}) {
  // Fetch invoice balance
  const {
    balance,
    isLoading: balanceLoading,
    error: balanceError,
    refetch: refetchBalance
  } = useInvoiceBalance(invoiceId, { autoFetch: open && !!invoiceId })

  // Fetch credit notes for this invoice
  const {
    creditNotes,
    isLoading: creditNotesLoading,
    error: creditNotesError,
    retrySyncToXero,
    refetch: refetchCreditNotes
  } = useCreditNotes({ invoiceId, autoFetch: open && !!invoiceId })

  // Local state for payments (fetched separately)
  const [payments, setPayments] = useState([])
  const [paymentsLoading, setPaymentsLoading] = useState(false)

  // Credit note modal state
  const [creditNoteModalOpen, setCreditNoteModalOpen] = useState(false)

  // Extract latest GoCardless payment info from payments array
  const gcPayment = useMemo(() => {
    const gc = payments.find(p => p.source === 'gocardless' && p.gocardless_payment_status)
    if (!gc) return null
    return {
      status: gc.gocardless_payment_status,
      chargeDate: gc.gocardless_charge_date,
      failureReason: gc.gocardless_failure_reason
    }
  }, [payments])

  // Fetch payments for this invoice
  useEffect(() => {
    if (!open || !invoiceId) {
      setPayments([])
      return
    }

    const fetchPayments = async () => {
      setPaymentsLoading(true)
      try {
        const { data, error } = await supabase
          .from('invoice_payments')
          .select('*')
          .eq('invoice_id', invoiceId)
          .order('date', { ascending: false })

        if (error) throw error
        setPayments(data || [])
      } catch (err) {
        console.error('[InvoiceDetailPanel] Error fetching payments:', err)
        setPayments([])
      } finally {
        setPaymentsLoading(false)
      }
    }

    fetchPayments()
  }, [open, invoiceId])

  // Refresh all data
  const handleRefresh = async () => {
    await Promise.all([refetchBalance(), refetchCreditNotes()])
    // Re-fetch payments
    if (invoiceId) {
      const { data } = await supabase
        .from('invoice_payments')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('date', { ascending: false })
      setPayments(data || [])
    }
  }

  // Handle credit note creation success
  const handleCreditNoteSuccess = async () => {
    await handleRefresh()
    if (onDataChange) {
      onDataChange()
    }
  }

  // Handle record payment click
  const handleRecordPayment = () => {
    if (onRecordPayment && balance) {
      onRecordPayment({
        id: invoiceId,
        invoice_no: balance.invoice_no,
        customer_id: balance.customer_id,
        customer_name: balance.customer_name,
        amount_due: balance.amount_due,
        is_legacy_paid: balance.is_legacy_paid
      })
    }
  }

  const isLoading = balanceLoading || creditNotesLoading || paymentsLoading
  const hasError = balanceError || creditNotesError

  // Build invoice object for CreditNoteModal
  const invoiceForModal = balance
    ? {
        id: invoiceId,
        invoice_no: balance.invoice_no,
        customer_id: balance.customer_id,
        customer_name: balance.customer_name,
        amount_due: balance.amount_due,
        is_legacy_paid: balance.is_legacy_paid
      }
    : null

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2">
                Invoice {balance?.invoice_no || '...'}
                {balance && (
                  <InvoiceStatusBadge status={balance.calculated_status} />
                )}
              </SheetTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                disabled={isLoading}
                aria-label="Refresh data"
              >
                <RefreshCwIcon
                  className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
                />
              </Button>
            </div>
            <SheetDescription>
              View payment history and manage credit notes
            </SheetDescription>
          </SheetHeader>

          {/* Loading State */}
          {isLoading && !balance && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
            </div>
          )}

          {/* Error State */}
          {hasError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700 mt-4">
              {balanceError || creditNotesError}
            </div>
          )}

          {/* Content */}
          {balance && (
            <div className="mt-6 space-y-6">
              {/* Legacy Invoice Banner */}
              {balance.is_legacy_paid && <LegacyInvoiceBanner />}

              {/* Invoice Summary */}
              <div className="rounded-lg border bg-gray-50 p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <UserIcon className="h-4 w-4" />
                  <span>{balance.customer_name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CalendarIcon className="h-4 w-4" />
                  <span>{formatDate(balance.invoice_date)}</span>
                </div>

                <Separator />

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Invoice Total</span>
                    <span className="font-medium">
                      {formatCurrency(balance.invoice_total)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Amount Paid</span>
                    <span className="font-medium text-green-600">
                      -{formatCurrency(balance.amount_paid)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Credits Applied</span>
                    <span className="font-medium text-blue-600">
                      -{formatCurrency(balance.amount_credited)}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="font-medium">Balance Due</span>
                    <span
                      className={`font-semibold ${
                        balance.amount_due > 0
                          ? 'text-amber-600'
                          : 'text-green-600'
                      }`}
                    >
                      {formatCurrency(balance.amount_due)}
                    </span>
                  </div>
                </div>

                {/* GoCardless payment status summary */}
                {gcPayment && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">GoCardless Status</span>
                        <GoCardlessStatusBadge type="payment" status={gcPayment.status} />
                      </div>
                      {gcPayment.chargeDate && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Charge Date</span>
                          <span className="font-medium">{formatDate(gcPayment.chargeDate)}</span>
                        </div>
                      )}
                      {gcPayment.failureReason && (
                        <p className="text-xs text-red-600">{gcPayment.failureReason}</p>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Payment/Credit History */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Payment &amp; Credit History
                </h3>
                <PaymentHistoryTimeline
                  payments={payments}
                  creditNotes={creditNotes}
                  invoiceTotal={balance.invoice_total}
                  onRetryCreditNoteSync={retrySyncToXero}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          {balance && (
            <SheetFooter className="mt-6 flex-col gap-2 sm:flex-col">
              <Button
                className="w-full"
                onClick={handleRecordPayment}
                disabled={balance.amount_due <= 0 && !balance.is_legacy_paid}
              >
                <CreditCardIcon className="h-4 w-4 mr-2" />
                Record Payment
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setCreditNoteModalOpen(true)}
                disabled={
                  balance.amount_due <= 0 &&
                  balance.calculated_status !== 'paid'
                }
              >
                <ReceiptIcon className="h-4 w-4 mr-2" />
                Create Credit Note
              </Button>
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>

      {/* Credit Note Modal */}
      {invoiceForModal && (
        <CreditNoteModal
          invoice={invoiceForModal}
          open={creditNoteModalOpen}
          onOpenChange={setCreditNoteModalOpen}
          onSuccess={handleCreditNoteSuccess}
        />
      )}
    </>
  )
}

export default InvoiceDetailPanel
